import React, {
    createContext, RefObject, useCallback, useContext, useMemo, useRef, useState,
} from "react";
import { useReactFlow, useNodesInitialized } from "@xyflow/react";
import { Offcanvas } from "react-bootstrap";
import ActionsSelector from "../components/ActionsSelector";
import Styles from "../AutomationFlow.module.scss";
import { useWorkflow } from "./WorkflowProvider";
import { ACTION } from "../workflow/nodes";

interface WorkflowSidebarContextType {
    toggleSidebarVisibility: (open: boolean) => void;
    containerRef: RefObject<HTMLDivElement> | null;
    resetTemporaryState: () => void;
}

const WorkflowSidebarContext = createContext<WorkflowSidebarContextType>({
    toggleSidebarVisibility: () => {
        // do nothing
    },
    resetTemporaryState: () => {
        // do nothing
    },
    containerRef: null,
});

export default function WorkflowSidebarProvider({ children }: { children: React.ReactNode }) {
    const {
        temporaryNodeId, clearSelection, setServiceConfiguration, deleteNodeFromWorkflow,
    } = useWorkflow();
    const { getNode } = useReactFlow();

    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const isReactFlowInitialized = useNodesInitialized({
        includeHiddenNodes: true,
    });

    const toggleSidebarVisibility = useCallback((open: boolean) => {
        setIsOpen(open);
    }, []);

    const resetTemporaryState = useCallback(() => {
        clearSelection();
        toggleSidebarVisibility(false);
        setServiceConfiguration(null);
        if (!temporaryNodeId) {
            return;
        }

        const nodeWithTemporaryId = getNode(temporaryNodeId);

        if (!nodeWithTemporaryId) {
            return;
        }

        const isNewActionNode = nodeWithTemporaryId.type === ACTION.NEW;

        if (!isNewActionNode) {
            return;
        }

        deleteNodeFromWorkflow({ nodeId: temporaryNodeId, shouldMarkDirty: false });
    }, [temporaryNodeId]);

    const value = useMemo(() => ({
        toggleSidebarVisibility,
        containerRef,
        resetTemporaryState,
    }), [toggleSidebarVisibility, containerRef, resetTemporaryState]);

    return (
        <WorkflowSidebarContext.Provider value={value}>
            {children}
            {isReactFlowInitialized && isOpen && (
                <Offcanvas
                    show={isOpen}
                    onHide={resetTemporaryState}
                    placement="end"
                    backdrop={false}
                    scroll
                    className={Styles.offcanvas}
                    container={containerRef}
                >
                    <Offcanvas.Body>
                        <ActionsSelector />
                    </Offcanvas.Body>
                </Offcanvas>
            )}
        </WorkflowSidebarContext.Provider>
    );
}

export function useWorkflowSidebar() {
    return useContext(WorkflowSidebarContext);
}
